/**
 * ConfirmDialog — Shadcn AlertDialog wrapper with controlled open state.
 *
 * Replaces ad-hoc `window.confirm()` calls so Playwright can drive confirmations
 * via data-testid instead of `page.on('dialog')` handlers.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <ConfirmDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Delete this location?"
 *     description="This will remove the location for all venue staff."
 *     confirmLabel="Delete"
 *     variant="destructive"
 *     onConfirm={() => doDelete()}
 *   />
 */
import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default', // 'default' | 'destructive'
  onConfirm,
  testIdPrefix = 'confirm-dialog',
}) => {
  const handleConfirm = async (e) => {
    e?.preventDefault?.();
    await onConfirm?.();
    onOpenChange?.(false);
  };

  return (
    <AlertDialog open={!!open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testIdPrefix}>
        <AlertDialogHeader>
          <AlertDialogTitle data-testid={`${testIdPrefix}-title`}>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription data-testid={`${testIdPrefix}-description`}>
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testIdPrefix}-cancel`}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testIdPrefix}-confirm`}
            onClick={handleConfirm}
            className={
              variant === 'destructive' ? 'bg-rose-600 hover:bg-rose-700 text-white' : undefined
            }
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
